import { skipToken } from '@reduxjs/toolkit/query';
import axios from 'axios';
import PermissionButton from 'components/buttons/PermissionButton';
import VacationButton from 'components/employee/VacationButton';
import VacationMarker from 'components/employee/VacationMarker';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import {
    ActiveChip,
    META_LINK_CLASS,
    MetaItem,
    OUTLINED_ACTION_CLASS,
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordHeader,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { EmployeeNameLink, RecordLink } from 'components/record-chip/RecordLink';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useGetEmployeeByIdQuery } from 'store/api/employees';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { formatEmployeeName, useLastNameFirst } from 'utils/nameOrder';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ImpersonateButton from './ImpersonateButton.tsx';
import { useEmployeePermissions } from './useEmployeePermissions';
import { GeneralView } from './views/GeneralView';
import { WebView } from './views/WebView';

const USED_CLASSIFIERS = ['PERSON_TITLE', 'GENDER_TYPE', 'LANGUAGE', 'COUNTRY', 'PARTNERSHIP_PEER_GROUP_TYPE'];

/** An employee picture as an object URL: the download needs the session, so an `<img>` cannot fetch it itself. */
function usePictureSrc(endpoint: string, pictureName: string | null | undefined) {
    const [src, setSrc] = useState<string | undefined>(undefined);

    // Download the picture as blob (mirrors ImageInput's approach for authenticated image streams).
    // TODO(react-doctor: no-fetch-in-effect): deliberate raw axios fetch — the download streams a
    // binary blob and converts to an object URL for an <img>; RTK Query's JSON cache is the wrong tool.
    // oxlint-disable-next-line react-doctor/no-cascading-set-state -- 3 setState calls but all target the same `src`, mutually exclusive branches of one effect
    useEffect(() => {
        let objectUrl: string | undefined;
        let cancelled = false;
        const load = async () => {
            if (!pictureName) {
                setSrc(undefined);
                return;
            }
            try {
                const response = await axios.get(`${import.meta.env.VITE_API_URL}${endpoint}${pictureName}`, {
                    withCredentials: true,
                    responseType: 'blob',
                });
                if (cancelled) return;
                objectUrl = URL.createObjectURL(response.data);
                setSrc(objectUrl);
            } catch {
                if (!cancelled) setSrc(undefined);
            }
        };
        void load();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [endpoint, pictureName]);

    return src;
}

const EmployeePage = (): JSX.Element => {
    const { id: employeeId } = useParams();
    const lastNameFirst = useLastNameFirst();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/employees');

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: employee, isLoading: isLoadingEmployee } = useGetEmployeeByIdQuery(employeeId ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const isDraft = !!employee?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/employees');
    const draftActions = useDraftActions({
        draft: employee?.isDraft ? { type: 'employee', id: employee.id } : null,
        createRoute: '/employee/new',
        onDeleted: leaveDeleted,
    });
    const {
        classifiers,
        allClassifiers,
        isLoading: isLoadingClassifiers,
    } = useClassifiers({ codes: USED_CLASSIFIERS });
    const { customFieldsFiltered, isLoading: isLoadingCustomFields } = useCustomFields({
        entityType: EntityType.EMPLOYEE,
        fields: employee?.customFields,
    });

    const isNew = useIsNewDesign();
    const { hasEmployeeUpdatePermission } = useEmployeePermissions(employeeId);
    const isSignatureEnabled = useIsFeatureEnabled(FeatureFlag.EMPLOYEE_SIGNATURE_FILE);
    const isWebTabEnabled = useIsFeatureEnabled(FeatureFlag.WEB_TAB_EMPLOYEES);
    const profilePictureSrc = usePictureSrc('/v1/employees/profile-pictures/', employee?.profilePictureName);
    // Only the new design shows the signature on the page; its card edits it.
    const signaturePictureSrc = usePictureSrc(
        '/v1/employees/signature-picture/',
        isNew && isSignatureEnabled ? employee?.signaturePictureName : null
    );

    const tabs = useMemo<TabType[]>(() => {
        if (!employee || !employeeId) return [];
        return [
            {
                id: 'general',
                label: t('employees.general'),
                component: (
                    <GeneralView
                        employee={employee}
                        classifiers={classifiers}
                        allClassifiers={allClassifiers}
                        customFields={customFieldsFiltered}
                        profilePictureSrc={profilePictureSrc}
                        signaturePictureSrc={signaturePictureSrc}
                        isNew={isNew}
                    />
                ),
            },
            {
                id: 'web',
                label: t('employees.web.tab'),
                // A draft has no web profile until it is created.
                hidden: !isWebTabEnabled || isDraft,
                component: <WebView employeeId={employeeId} classifiers={classifiers} />,
            },
        ];
    }, [
        employee,
        employeeId,
        classifiers,
        allClassifiers,
        customFieldsFiltered,
        profilePictureSrc,
        signaturePictureSrc,
        isNew,
        isWebTabEnabled,
        isDraft,
    ]);

    if (isLoadingEmployee || isLoadingClassifiers || isLoadingCustomFields) {
        return <Loader />;
    }

    if (!employee) {
        return <></>;
    }

    const tabsGallery = (
        <TabsGallery
            tabs={tabs}
            tabGroupId="employee-page"
            trackTabsInHistoryState
            usePathBasedRouting
            wrapperClassName={RECORD_TABS_CLASS}
            panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
            className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
        />
    );

    const title =
        formatEmployeeName({ ...employee, onVacation: false }, lastNameFirst) ||
        (isDraft ? t('new-entity.employee') : '-');
    const draft = draftActions.draft;
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;
    const titleNode = (
        <>
            <VacationMarker onVacation={employee.onVacation} />
            {title}
        </>
    );
    const impersonateButtonProps = {
        employeeId,
        workEmailAddress: employee.workEmailAddress,
        cognitoId: employee.cognitoId,
        isActive: employee.isActive,
        canBeImpersonatedBy: employee.canBeImpersonatedBy,
    };

    if (isNew) {
        const phone = employee.workPhone || employee.mobilePhone;
        const header = (
            <RecordHeader
                chips={
                    <>
                        {isDraft ? <DraftChip /> : null}
                        <ActiveChip isActive={employee.isActive} />
                    </>
                }
                title={title}
                pin={isDraft ? undefined : { kind: 'employee', id: employee.id }}
                titleNode={titleNode}
                meta={
                    <>
                        {employee.manager?.id ? (
                            <MetaItem label={<FormattedMessage id="supervisor" />}>
                                <EmployeeNameLink
                                    className={META_LINK_CLASS}
                                    to={`/employee/${employee.manager.id}`}
                                    entity={employee.manager}
                                />
                            </MetaItem>
                        ) : null}
                        {employee.coreTeam?.id ? (
                            <MetaItem label={<FormattedMessage id="teams.core-team" />}>
                                <RecordLink
                                    className={META_LINK_CLASS}
                                    to={`/team/${employee.coreTeam.id}`}
                                    label={employee.coreTeam.name}
                                />
                            </MetaItem>
                        ) : null}
                        {employee.workEmailAddress ? (
                            <MetaItem label={<FormattedMessage id="email" />}>
                                <a className={META_LINK_CLASS} href={`mailto:${employee.workEmailAddress}`}>
                                    {employee.workEmailAddress}
                                </a>
                            </MetaItem>
                        ) : null}
                        {phone ? <MetaItem label={<FormattedMessage id="phone-number" />}>{phone}</MetaItem> : null}
                        {draft ? <DraftMeta draft={draft} /> : null}
                    </>
                }
                actions={
                    // A draft has no account yet, so nothing to go on vacation from or to sign in as.
                    isDraft ? (
                        <DraftRecordActions actions={draftActions} />
                    ) : (
                        <>
                            <VacationButton employeeId={employee.id} onVacation={employee.onVacation} />
                            <ImpersonateButton
                                {...impersonateButtonProps}
                                className={cn(OUTLINED_ACTION_CLASS, 'px-3.5 font-medium')}
                            />
                        </>
                    )
                }
            />
        );
        return (
            <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
                <RecordPage
                    header={
                        draftNotice ? (
                            <div className="flex flex-col gap-4">
                                {header}
                                {draftNotice}
                            </div>
                        ) : (
                            header
                        )
                    }
                >
                    <SectionEditProvider canEdit={hasEmployeeUpdatePermission && !isDraft}>
                        {tabsGallery}
                    </SectionEditProvider>
                </RecordPage>
                {draftActions.shareDialog}
            </DraftActionsContext.Provider>
        );
    }

    return (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <PaddedLayout className="flex h-full flex-col overflow-hidden">
                <PageHeader
                    title={title}
                    titleNode={titleNode}
                    showBreadcrumb
                    onBack={goBack}
                    buttons={
                        isDraft ? (
                            <div className="flex items-center justify-end gap-2">
                                <DraftRecordActions actions={draftActions} />
                            </div>
                        ) : (
                            <div className="flex flex-row justify-end gap-2">
                                <VacationButton employeeId={employee.id} onVacation={employee.onVacation} />
                                <ImpersonateButton {...impersonateButtonProps} />
                                <PermissionButton
                                    permissions={['UPDATE.EMPLOYEES', 'UPDATE.EMPLOYEE_PERSONAL.ALL']}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigateWithReturn(`/employee/${employeeId}/edit`)}
                                >
                                    <FormattedMessage id="edit" />
                                </PermissionButton>
                            </div>
                        )
                    }
                >
                    {draft ? (
                        <div className="flex flex-wrap items-center gap-2 py-3">
                            <DraftChip />
                            <DraftMeta draft={draft} />
                        </div>
                    ) : null}
                </PageHeader>
                {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
                {tabsGallery}
            </PaddedLayout>
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );
};

export default EmployeePage;
